/**
 * L'escalier de déverrouillage — **avenant A-5, proposition 5** (validée le 14/08).
 *
 * « Le sentiment de progression ne vient pas de chiffres flattés : il vient de ce que le jeu
 * s'ouvre. » Au départ le jeu tient en **deux boutons** (publier un short, dormir) ; les
 * mécaniques arrivent une à une pour qu'à 1 000 abonnés elles soient **toutes** là.
 *
 * Deux exigences de l'avenant sont portées ici :
 * - une mécanique non déverrouillée est **absente**, pas grisée (la seule exception est la
 *   jauge Argent, que l'avenant **A-1** point 8 demande explicitement grisée + tooltip) ;
 * - chaque ouverture est **un moment de jeu**, jamais un silence : d'où le champ `annonce`.
 *
 * **Où vivent les seuils.** ⟦A-9⟧ **Tous** les étages tiennent désormais leur seuil du
 * moteur (`PUBLICATIONS_ESCALIER`, `shared/src/engine/escalier.ts`) et le comptent en
 * **publications**, pas en abonnés : c'est la seule chose que le débutant contrôle, et les
 * abonnés refermaient la boucle sur elle-même (le plafond de compétence écrase la qualité ⇒
 * moins d'abonnés ⇒ l'étage ne s'ouvre jamais — mesuré). Ce fichier ne recopie plus un seul
 * chiffre : il lit `mecaniqueOuverte`, exactement comme `validateAction`.
 *
 * ⚠️ **Seule exception, signalée et non tranchée : `argent`.** Son ouverture EST « Le Grand
 * Saut » ⟦A-1⟧, un événement scripté attaché au palier 1 000 abonnés qui verse le capital et
 * allume les charges. Le décrocher du palier ferait apparaître une boîte à Clicks vide. A-9
 * ancre d'ailleurs lui-même son arc 3 à « 1 000 — l'entreprise ». La contradiction est
 * remontée à Mathieu (CLAUDE.md : on signale, on ne tranche pas seul).
 */

import { mecaniqueOuverte, type GameState, type SystemeId, type Systemes } from "@maxime/shared";

import { interrupteurs } from "./menage";

export type EtageId =
  | "veille"
  | "commentaires"
  | "image"
  | "tendances"
  | "arbre"
  | "longue"
  | "argent";

export interface Etage {
  id: EtageId;
  /** Numéro d'étage dans la table de l'avenant A-5 (l'étage 0 est le départ à deux boutons). */
  rang: number;
  titre: string;
  /**
   * ⟦C57 · lot 3 · A-174⟧ Le titre de la pop-up d'ouverture quand `titre` dépasse 5 mots (la règle
   * « moins de texte ») : écrit À CÔTÉ du long, jamais à sa place. Absent = `titre` tel quel.
   */
  titre_court?: string;
  /** Le moment de jeu : ce que le joueur lit quand ça s'ouvre. */
  annonce: string;
  /**
   * ⟦C57 · lot 3 · A-174⟧ **L'annonce COURTE** : une phrase de 12 mots au plus, c'est tout ce que la
   * pop-up d'ouverture garde à l'écran. `annonce` (le texte long) n'est pas réécrite : elle reste
   * telle quelle, lue par `paragraphes()` (la leçon du mode découverte) et par le Journal, où
   * l'entrée d'ouverture la garde derrière « Les dessous ».
   */
  annonce_courte: string;
  icone: string;
  /**
   * ⟦A-7 décision 5⟧ **L'escalier est PHYSIQUE** : chaque déverrouillage fait apparaître un
   * objet dans la chambre. Le joueur ne lit pas « mécanique débloquée » — il voit sa chambre
   * se remplir. C'est le nom de l'objet qui apparaît, tel qu'on le dit au joueur.
   */
  objet: string;
  /** Le déclencheur, en français, pour l'écran de progression. */
  condition: string;
  ouvert(state: GameState): boolean;
  /**
   * ⟦C60 · A-177⟧ **Le système dont cet étage n'est que la vitrine.** Quand le ménage le coupe, l'étage
   * n'existe plus : ni objet dans la chambre, ni annonce à l'ouverture, ni ligne dans « Ta chambre a
   * gagné », ni « prochaine ouverture ». `ouvert()` rend `false` (⟦A-5⟧ : ce qui n'est pas déverrouillé
   * est absent), le moteur, lui, garde sa mécanique intacte. Absent = l'étage est toujours là.
   */
  absentSi?: SystemeId;
  /**
   * ⟦C60 · A-177⟧ **Ce que l'étage dit quand son système est coupé**, à la place du texte d'avant : l'étage
   * reste (la vidéo longue, les Clicks), seul ce qu'il PROMETTAIT change (plus d'étagère, plus de boîte à
   * Clicks, plus de charges). Le texte d'avant reste ci-dessus, mot pour mot : le rallumer le ramène.
   */
  sansSysteme?: {
    systeme: SystemeId;
    titre: string;
    objet: string;
    annonce: string;
    annonce_courte: string;
  };
}

/**
 * La table de l'avenant A-5. **Les seuils sont des valeurs de départ**, ajustables au
 * playtest sans repasser par un avenant (A-5, en-tête) : ils sont donc tous ici, en clair.
 *
 * ⟦C60 · A-177⟧ C'est la table COMPLÈTE, celle d'avant le ménage : `ESCALIER` (plus bas) en est la vue du
 * contenu chargé, sans les étages dont le système est coupé et avec le texte de ceux qui changent.
 */
const TOUS_LES_ETAGES: readonly Etage[] = [
  {
    id: "veille",
    rang: 1,
    titre: "La veille",
    icone: "🔭",
    objet: "la fenêtre",
    condition: "après ta 1re publication",
    annonce:
      "Tu as publié. Tu lèves les yeux : il y a une fenêtre dans cette chambre. Regarder dehors — voir ce que font les autres — ça donne des idées.",
    annonce_courte: "Une fenêtre apparaît : regarder dehors donne des idées.",
    ouvert: (s) => mecaniqueOuverte(s, "veille"),
  },
  {
    id: "commentaires",
    rang: 2,
    titre: "Lire les commentaires",
    icone: "💬",
    objet: "le téléphone sur le bureau",
    condition: "à ta 3e publication",
    annonce:
      "Il y en a enfin. Ton téléphone traîne sur le bureau : tu peux lire tes commentaires. Ça pique parfois, mais ça donne des idées.",
    annonce_courte: "Ton téléphone traîne sur le bureau : lis tes commentaires.",
    ouvert: (s) => mecaniqueOuverte(s, "lire_commentaires"),
  },
  {
    id: "image",
    rang: 3,
    titre: "L'image de chaîne",
    icone: "🎭",
    objet: "le poster au-dessus du bureau",
    condition: "à ta 4e publication",
    annonce:
      "Quatre vidéos en ligne. Tu punaises un poster au-dessus du bureau : à partir de maintenant, ce que tu publies dessine une ligne — lumière ou chaos.",
    annonce_courte: "Un poster au mur : ce que tu publies dessine une ligne.",
    ouvert: (s) => mecaniqueOuverte(s, "image"),
    // ⟦C60 · A-177⟧ L'image de chaîne EST l'alignement (lumière ou chaos) : coupé, plus de poster, plus de
    // « Ma ligne », plus d'annonce. L'étage disparaît de l'escalier au lieu de promettre une ligne à tenir.
    absentSi: "alignement",
  },
  {
    id: "tendances",
    rang: 4,
    titre: "Les tendances",
    icone: "🔥",
    objet: "le tableau des tendances, au mur",
    condition: "à ta 5e publication",
    annonce:
      "Tu commences à regarder ce qui marche. Un tableau apparaît au mur : trois tendances y tournent en permanence — surfe, ou pas.",
    annonce_courte: "Un tableau au mur : trois tendances y tournent en permanence.",
    ouvert: (s) => mecaniqueOuverte(s, "tendances"),
  },
  {
    id: "arbre",
    rang: 5,
    titre: "L'arbre de compétences",
    icone: "🌳",
    objet: "le panneau de liège",
    condition: "à ta 6e publication",
    annonce:
      "Six vidéos. Un panneau de liège se remplit à côté du bureau : tes Points de Créativité ont enfin où aller. À toi de choisir ta patte.",
    annonce_courte: "Un panneau de liège : tes Points de Créativité servent enfin.",
    ouvert: (s) => mecaniqueOuverte(s, "arbre"),
  },
  {
    id: "longue",
    rang: 6,
    titre: "La vidéo longue et la bibliothèque",
    titre_court: "La vidéo longue",
    icone: "🎬",
    objet: "l'étagère",
    condition: "à ta 10e publication",
    annonce:
      "Dix vidéos au compteur. Une étagère se monte contre le mur : tu peux passer aux vidéos longues, et ton catalogue commence à travailler pour toi.",
    annonce_courte: "Une étagère : vidéos longues et catalogue, c'est ouvert.",
    ouvert: (s) => mecaniqueOuverte(s, "longue"),
    // ⟦C60 · A-177⟧ La gestion de la bibliothèque est coupée (plus d'étagère dans la chambre, plus d'écran) :
    // la vidéo longue, elle, reste, et passe par le bouton Nouvelle vidéo comme le short. Les vieilles vidéos
    // continuent de rapporter leurs vues sans qu'on les gère.
    sansSysteme: {
      systeme: "bibliotheque",
      titre: "La vidéo longue",
      objet: "la vidéo longue, au bureau",
      annonce:
        "Dix vidéos au compteur. Tu peux passer aux vidéos longues : plus d'étapes et plus d'heures, mais une tout autre échelle de vues.",
      annonce_courte: "Les vidéos longues sont ouvertes : le bouton Nouvelle vidéo les propose.",
    },
  },
  {
    id: "argent",
    rang: 7,
    titre: "L'argent",
    icone: "₵",
    objet: "la boîte à Clicks, sur l'étagère",
    condition: "à 1 000 abonnés (Le Grand Saut)",
    // ⟦A-4 arbitrage A — le twist des 4 000 heures, VERSION TEXTE⟧ La monétisation réelle ne
    // s'ouvre pas sur les abonnés seuls : il faut aussi 4 000 h de visionnage cumulées, et
    // beaucoup de chaînes ont les uns sans les autres (trop de shorts). Mathieu a tranché la
    // version texte : la narration le raconte, **aucun compteur ne conditionne le Grand Saut**
    // (la version mécanique reste post-v1). La même phrase est portée par l'événement
    // `demission_job` de `content/events.json` — les deux doivent rester d'accord.
    annonce:
      "Le Grand Saut. Tu quittes le Tasty Crusty, tes parents t'avancent de quoi tenir. Une boîte à Clicks apparaît sur l'étagère : revenus ET charges, la couche argent s'active. Et tu es passé de justesse sur les quatre mille heures de visionnage — heureusement que tu as fait des longues.",
    annonce_courte: "Le Grand Saut : une boîte à Clicks, la couche argent s'active.",
    ouvert: (s) => s.flags.grand_saut_done,
    // ⟦C60 · A-177⟧ Plus de loyer, plus de charges, plus de capital, plus de boîte sur l'étagère : l'argent
    // devient « des Clicks pour payer », et rien d'autre. Le palier de 1 000 abonnés reste un palier : c'est
    // lui qui ouvre le compteur de Clicks et le Marché des freelances.
    sansSysteme: {
      systeme: "charges",
      titre: "Les Clicks",
      objet: "le compteur de Clicks",
      annonce:
        "Le Grand Saut. Tu quittes le Tasty Crusty pour de bon. Tes vues rapportent désormais des Clicks : de quoi payer le Marché des freelances et ton équipe. Et tu es passé de justesse sur les quatre mille heures de visionnage : heureusement que tu as fait des longues.",
      annonce_courte: "Le Grand Saut : tes vues rapportent des Clicks pour payer le Marché.",
    },
  },
];

const PAR_ID = new Map(TOUS_LES_ETAGES.map((e) => [e.id, e] as const));

/**
 * ⟦C60 · A-177⟧ L'étage tel que le ménage le dit : son texte d'avant, ou celui de son système coupé
 * (`sansSysteme`). Rend la MÊME instance quand rien n'est coupé : l'état neutre, au caractère près.
 */
function resolu(e: Etage, sys: Systemes): Etage {
  const v = e.sansSysteme;
  if (!v || sys[v.systeme]) return e;
  const { titre_court: _perdu, ...reste } = e;
  return { ...reste, titre: v.titre, objet: v.objet, annonce: v.annonce, annonce_courte: v.annonce_courte };
}

/** Cet étage a-t-il disparu parce que son système est coupé ? (`sys` : les interrupteurs du contenu.) */
function absent(e: Etage, sys: Systemes): boolean {
  return e.absentSi !== undefined && !sys[e.absentSi];
}

/**
 * ⟦C60 · A-177⟧ **L'escalier d'un jeu donné** : les étages qui existent encore, avec leurs textes à jour.
 * Pure, pour que les tests rejouent aussi le jeu d'AVANT (`SYSTEMES_TOUS_ACTIFS` rend la table d'origine,
 * sept étages, mot pour mot).
 */
export function escalierPour(sys: Systemes): readonly Etage[] {
  return TOUS_LES_ETAGES.filter((e) => !absent(e, sys)).map((e) => resolu(e, sys));
}

/**
 * L'escalier du contenu chargé. Le contenu est un import statique, jamais rechargé à chaud : on peut donc
 * le calculer une fois, et les écrans qui parcourent la table (Journal, tests) voient la même chose que
 * `ouvert()`, `nouveauxEtages()` et `prochainEtage()`.
 */
export const ESCALIER: readonly Etage[] = escalierPour(interrupteurs());

export function etage(id: EtageId): Etage {
  const e = PAR_ID.get(id);
  if (!e) throw new Error(`étage d'escalier inconnu : ${id}`);
  return resolu(e, interrupteurs());
}

/**
 * Un étage est-il ouvert dans cet état ? La question que posent tous les écrans.
 * ⟦C60 · A-177⟧ Un étage dont le système est coupé n'est jamais ouvert (il n'existe plus).
 */
export function ouvert(state: GameState, id: EtageId): boolean {
  const e = PAR_ID.get(id);
  if (!e) throw new Error(`étage d'escalier inconnu : ${id}`);
  return !absent(e, interrupteurs()) && e.ouvert(state);
}

export function etagesOuverts(state: GameState): EtageId[] {
  return ESCALIER.filter((e) => e.ouvert(state)).map((e) => e.id);
}

/**
 * Les étages qui viennent de s'ouvrir — ceux dont l'ouverture n'a pas encore été **annoncée**.
 * `dejaVus` vit dans la run (pas dans `GameState`) : un F5 ne rejoue pas les annonces.
 */
export function nouveauxEtages(state: GameState, dejaVus: readonly EtageId[]): EtageId[] {
  const vus = new Set(dejaVus);
  return etagesOuverts(state).filter((id) => !vus.has(id));
}

/** Le prochain étage à ouvrir — sert à l'écran de progression, jamais à teaser une mécanique. */
export function prochainEtage(state: GameState): Etage | null {
  return ESCALIER.find((e) => !e.ouvert(state)) ?? null;
}
